import { useState, useEffect, useRef, useCallback } from 'react';
import { categoriesApi, productsApi } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Global categories cache — persists across route changes
const globalCategoriesCache = {};

// Default category images
export const categoryImages = {
  'cat-picar': 'https://images.unsplash.com/photo-1626200419199-391ae4be7a41?w=600&q=80',
  'cat-ensaladas': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=600&q=80',
  'cat-bocadillos-tradicionales': 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=600&q=80',
  'cat-bocadillos-especiales': 'https://images.unsplash.com/photo-1553909489-cd47e0907980?w=600&q=80',
  'cat-pizzas': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&q=80',
  'cat-dulces': 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=600&q=80',
  'cat-cervezas': 'https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=600&q=80',
  'cat-bodega': 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=600&q=80',
  'cat-licores': 'https://images.unsplash.com/photo-1569529465841-dfecdab7503b?w=600&q=80',
  'cat-cocteles': 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&q=80',
  'cat-cafe-plantas': 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&q=80',
  'cat-reposteria': 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=600&q=80',
  'cat-refrescos': 'https://images.unsplash.com/photo-1625772299848-391b6a87d7b3?w=600&q=80',
};

const productPlaceholders = [
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80',
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=400&q=80',
  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=400&q=80',
];

// Pre-fetch all section categories and images
export const prefetchCategories = async () => {
  const sections = ['degustacion', 'bar', 'cafe', 'tienda'];
  for (const section of sections) {
    if (globalCategoriesCache[section]) continue;
    try {
      const res = await fetch(`${API_URL}/api/categories?parent_type=${section}`);
      if (res.ok) {
        const data = await res.json();
        globalCategoriesCache[section] = data;
        data.forEach(cat => {
          const src = cat.image || categoryImages[cat.id] || categoryImages['cat-picar'];
          if (src) { const img = new Image(); img.src = src; }
        });
      }
    } catch (e) { /* silent */ }
  }
};

export const getProductImage = (product, index) => {
  if (product.image_url && product.image_url.startsWith('http')) return product.image_url;
  if (product.images?.[0]) return product.images[0];
  return productPlaceholders[index % productPlaceholders.length];
};

export const useCategoryData = (experienceId, selectedCategory) => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const productsCache = useRef({});
  const hasLoadedOnce = useRef(false);

  const loadCategories = useCallback(async () => {
    if (!experienceId) return;
    try {
      const response = await categoriesApi.getAll({ parent_type: experienceId });
      const data = response.data || [];
      globalCategoriesCache[experienceId] = data;
      setCategories(data);
      if (!hasLoadedOnce.current && data.length > 0) {
        setTimeout(() => { hasLoadedOnce.current = true; }, 600);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to cache only on error
      if (globalCategoriesCache[experienceId]) {
        setCategories(globalCategoriesCache[experienceId]);
      } else {
        setCategories([]);
      }
    }
  }, [experienceId]);

  const hasProducts = useRef(false);

  const loadProducts = useCallback(async (catId) => {
    if (!catId) return;
    try {
      if (hasProducts.current) setLoading(true);
      const response = await productsApi.getAll({ category_id: catId });
      const filtered = (response.data || []).filter(p => p.product_type !== 'experience');
      productsCache.current[catId] = filtered;
      hasProducts.current = filtered.length > 0;
      setProducts(filtered);
      setCarouselIndex(0);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => {
    if (selectedCategory) {
      // Always fetch fresh from API to respect sort_order changes
      loadProducts(selectedCategory);
    }
  }, [selectedCategory, loadProducts]);

  return {
    categories, products, loading,
    carouselIndex, setCarouselIndex,
    hasLoadedOnce: hasLoadedOnce.current
  };
};
